import moment from "moment";
import {
    DATE_DISPLAY_FORMAT,
    TIME_DISPLAY_FORMAT,
    DATETIME_DISPLAY_FORMAT,
    enDash,
    YEAR_MONTH_DAY_FORMAT
} from "./constant";
import {pad} from "./number";

export const weekdaysMap = {
    Sunday: 0,
    Monday: 1,
    Tuesday: 2,
    Wednesday: 3,
    Thursday: 4,
    Friday: 5,
    Saturday: 6
};

// TODO: [DATE_FORMATTING] this function is not used. Check if it will be used and if we need to change the formatting.
export const getDateRange = (start, end) => {
    let dateArray = [];

    if (start !== end) {
        let startDate = start;
        const endDate = end;
        while (startDate <= endDate) {
            dateArray.push(startDate);
            startDate = getYearMonthDayFormat(moment(startDate).add(1, "days"));
        }
    }
    return dateArray;
};

// get dif between 2 dates on 'days'
export const calculateDays = (start, end) => {
    let startDate = moment(start, YEAR_MONTH_DAY_FORMAT);
    let endDate = moment(end, YEAR_MONTH_DAY_FORMAT);
    if (startDate.isValid() && endDate.isValid()) {
        return (endDate.diff(startDate, "days"));
    } else {
        return (null);
    }
};

export const formatDisplayDate = (value, inputFormat) => {
    let momentObj;
    if (!inputFormat) {
        momentObj = moment(value);
    } else {
        momentObj = moment(value, inputFormat);
    }
    return momentObj.format(DATE_DISPLAY_FORMAT);
};

export const formatDisplayTime = (value, inputFormat, isLocalized) => {
    let momentObj;
    if (!inputFormat) {
        momentObj = moment(value, moment.HTML5_FMT.TIME);
    } else {
        momentObj = moment(value, inputFormat);
    }
    if (isLocalized) {
        const hrs = -(new Date().getTimezoneOffset() / 60);
        momentObj?.add(hrs, "hours");
    }
    return momentObj?.format(TIME_DISPLAY_FORMAT);
};

export const formatDisplayDateTime = (value, inputFormat, isLocalized) => {
    let momentObj;
    if (!inputFormat) {
        momentObj = moment(value);
    } else {
        momentObj = moment(value, inputFormat);
    }
    if (isLocalized) {
        const hrs = -(new Date().getTimezoneOffset() / 60);
        momentObj?.add(hrs, "hours");
    }
    return momentObj?.format(DATETIME_DISPLAY_FORMAT);
};

export const getShortTranslatedDays = (daysList, locale) => {
    const consecutiveNumbers = getGroupOfConsecutiveNumbers(daysList?.map((day) => weekdaysMap[day]).sort());
    let response = "";
    consecutiveNumbers.forEach((numbers) => {
        if (numbers.length > 2) {
            response += `${getShortDate(numbers[0], locale)} ${enDash} ${getShortDate(numbers[numbers.length - 1], locale)}, `;
        } else {
            numbers.forEach((number) => {
                response += getShortDate(number, locale) + ", ";
            });
        }
    });
    return response.slice(0, -2);
};

export const parseWeekdays = (weekdays, locale, allDaysLabel) => {
    if (weekdays) {
        if (weekdays.length === 7) {
            return allDaysLabel;
        }
        return getShortTranslatedDays(weekdays, locale);
    }
};

// returns a date object in UTC timezone from date object or string (yyyy-MM-dd)
export const dateToUTCTimezone = (dateParam) => {
    const hour = 1;
    const minute = 0;
    const second = 0;
    let date;
    if ((typeof dateParam).toLowerCase() === "string") {
        const dateSplitArray = getDateSplitForUTCTimezone(dateParam);
        date = new Date(Date.UTC(dateSplitArray[0], dateSplitArray[1], dateSplitArray[2], hour, minute, second));
    } else if (dateParam instanceof moment) {
        date = new Date(Date.UTC(dateParam.year(), dateParam.month(), dateParam.date(), hour, minute, second));
    } else {
        date = new Date(Date.UTC(dateParam?.getFullYear(), dateParam?.getMonth(), dateParam?.getDate(), hour, minute, second));
    }

    return new Date(date.toLocaleString("en-US", {timeZone: "UTC"}));
};

// returns a date object to URL friendly format YYYY-MM-DD
export const dateToURLDateString = (date) => {

    let offset = date.getTimezoneOffset();
    let d = new Date(date.getTime() - (offset * 60 * 1000));
    return d.toISOString().split("T")[0];

};

const getDateSplitForUTCTimezone = (dateString) => {
    const dateSplit = dateString.split("-");
    dateSplit[1] = dateSplit[1] - 1;
    return dateSplit;
};

// Determines if some date is today
export const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear();
};

// Function that returns the YYYY-MM-DD format of a moment day object.
// IMPORTANT: this should not be used for rendering dates to the UI, as we use the Internationalization API for that.
export const getYearMonthDayFormat = (value) => {
    const dateMoment = value instanceof moment ? value : moment(value);
    return dateMoment?.format(YEAR_MONTH_DAY_FORMAT);
};

// returns a list of lists of consecutive numbers
const getGroupOfConsecutiveNumbers = (days) => {
    return days.reduce((r, n) => {
        const lastSubArray = r[r.length - 1];
        if (!lastSubArray || lastSubArray[lastSubArray.length - 1] !== n - 1) {
            r.push([]);
        }
        r[r.length - 1].push(n);
        return r;
    }, []);
};

export const getShortDate = (dayNumber, locale) => {
    // workaround: converting day number to date to utilize js date translations
    const shortDateToDisplay = dateToUTCTimezone("2021-01-" + ("1" + dayNumber)).toLocaleString(locale, {weekday: "short"});

    if (locale.startsWith("de")) {
        return shortDateToDisplay;
    }
    return shortDateToDisplay.slice(0, -1);
};


export const isDateRangeInClosingTimes = (from, to, closingTimes) => {
    if (!closingTimes || !from || !to) {
        return false;
    }

    // Get array of dates from - to
    const datesRange = getDateRange(from, to);

    // Get array of dates from the closing times
    let closingDatesRange = [];
    closingTimes?.items.forEach((times) => {
        // This collection can have entries of null value (due to contentful)
        if (times !== null) {
            const from = times.from.split("T")[0];
            const to = times.to.split("T")[0];
            const timesDatesRange = getDateRange(from, to);
            if (timesDatesRange) {
                closingDatesRange.push(...timesDatesRange);
            }
        }
    });

    // Return true, when at least one date in "datesRange" exist in closingTimes date range
    return closingDatesRange.some(date => datesRange.includes(date));
};

export const areDateRangesOverlapping = (fromFirst, toFirst, fromSecond, toSecond) => {

    const firstDateRange = getDateRange(fromFirst, toFirst);
    const secondDateRange = getDateRange(fromSecond, toSecond);

    // Determines if the date ranges have similar dates
    const overlap = firstDateRange.some(r => secondDateRange.includes(r));

    return overlap;
};

export const generateMonthsBetweenDates = (startDateString, endDateString) => {
    const startDate = new Date(startDateString);
    const endDate = new Date(endDateString);
    const startYear = startDate.getFullYear();
    const endYear = endDate.getFullYear();
    const dates = [];

    for (let i = startYear; i <= endYear; i++) {
        const endMonth = i !== endYear ? 11 : endDate.getMonth();
        const startMonth = i === startYear ? startDate.getMonth() : 0;
        for (let j = startMonth; j <= endMonth; j = j > 12 ? j % 12 || 11 : j + 1) {
            const month = j + 1;
            const displayMonth = month < 10 ? "0" + month : month;
            dates.push([i, displayMonth].join("-"));
        }
    }
    return dates;
};

export const isWeekendDay = (dateString) => {
    return [0, 6].includes(moment(dateString).weekday());
};

export const createDaysForCurrentMonth = (year, month) => {
    return [...Array(moment(`${year}-${pad(month)}-01`).daysInMonth())].map((_, index) => {
        return {
            dateString: `${year}-${pad(month)}-${pad(index + 1)}`,
            dayOfMonth: index + 1,
        };
    });
};

export const createDaysForPreviousMonth = (year, month, currentMonthDays) => {
    const currentMonthMoment = moment(currentMonthDays[0].dateString);
    const firstDayOfTheMonthWeekday = currentMonthMoment.weekday(); // sunday === 0, saturday === 6
    const visibleNumberOfDaysFromPreviousMonth = firstDayOfTheMonthWeekday ? firstDayOfTheMonthWeekday - 1 : 6;
    const previousMonthLastMondayDayOfMonth = currentMonthMoment.subtract(visibleNumberOfDaysFromPreviousMonth, "day")?.date();

    const previousYear = (month === 1) ? year - 1 : year;
    const previousMonth = (month === 1) ? 12 : month - 1;

    return [...Array(visibleNumberOfDaysFromPreviousMonth)].map((_, index) => {
        return {
            dateString: `${previousYear}-${pad(previousMonth)}-${pad(previousMonthLastMondayDayOfMonth + index)}`,
            dayOfMonth: previousMonthLastMondayDayOfMonth + index,
            isNotCurrentMonth: true,
        };
    });
};

export const createDaysForNextMonth = (year, month, currentMonthDays) => {
    const lastDayOfTheMonthWeekday = moment(`${year}-${pad(month)}-${currentMonthDays}`).weekday(); // sunday === 0, saturday === 6
    const visibleNumberOfDaysFromNextMonth = lastDayOfTheMonthWeekday ? 7 - lastDayOfTheMonthWeekday : lastDayOfTheMonthWeekday;

    const nextYear = (month === 12) ? year + 1 : year;
    const nextMonth = (month === 12) ? 1 : month + 1;

    return [...Array(visibleNumberOfDaysFromNextMonth)].map((day, index) => {
        return {
            dateString: `${nextYear}-${pad(nextMonth)}-${pad(index + 1)}`,
            dayOfMonth: index + 1,
            isNotCurrentMonth: true,
        };
    });
};

export const generateCalendarDays = (year, month) => {
    const currentMonth = createDaysForCurrentMonth(year, month);
    const previousMonth = createDaysForPreviousMonth(year, month, currentMonth);
    const nextMonth = createDaysForNextMonth(year, month, currentMonth.length);

    return [...previousMonth, ...currentMonth, ...nextMonth];
};

export const isValidDate = (d) => {
    return d instanceof Date && !isNaN(d);
};

export const isFutureDate = (value) => {
    let dateToCheck = value;
    if(typeof value === "string") {
        dateToCheck = new Date(value);
    }

    let res = null;
    if(isValidDate(dateToCheck)) {
        res = new Date().setHours(0, 0, 0, 0) - dateToCheck.setHours(0, 0, 0, 0) < 0;
    }

    return res;
};

export const isPastDate = (value) => {
    let dateToCheck = value;
    if(typeof value === "string") {
        dateToCheck = new Date(value);
    }

    let res = null;
    if(isValidDate(dateToCheck)) {
        res = new Date().setHours(0, 0, 0, 0) - dateToCheck.setHours(0, 0, 0, 0) > 0;
    }

    return res;
};

export const getNameOfDay = (date, locale = "en-GB") => {
    if (!isValidDate(date)) {
        return undefined;
    }

    const options = { weekday: "long" };
    const dayName = date.toLocaleString(locale, options);

    return dayName;
};

// Returns true if the date is the last day of the month
export const isLastDayOfMonth = (date) => {
    const momentDate = moment(date);
    const lastDayOfMonth = momentDate.clone().endOf("month");

    return momentDate.isSame(lastDayOfMonth, "day");
};

// Returns the offset in minutes from UTC for the current date. It is negative if the timezone is ahead of UTC.
export const getCETOffset = () => {
    const date = new Date();
    const options = {
        timeZone: "Europe/Berlin",
        timeZoneName: "short"
    };
    const formatter = new Intl.DateTimeFormat("en-GB", options);
    const offset = formatter.formatToParts(date).find(part => part.type === "timeZoneName").value;

    if (offset === "CET") {
        return -60;
    } else if (offset === "CEST") {
        return -120;
    } else {
        return 0; // Default to 0 for unknown cases
    }
};

// Converts a local date to CET
export const convertLocalToCET = (localDateTime) => {
    const localOffset = localDateTime.getTimezoneOffset();
    const cetOffset = getCETOffset();
    const timeDifference = (localOffset < cetOffset ? "-" : "") + Math.abs(cetOffset - localOffset);
    const cetDateTime = new Date(localDateTime.getTime() + timeDifference * 60 * 1000);

    return cetDateTime;
};

// Returns the year in number from a datestring of type YYYY-MM-DD
export const getYearFromDateString = (dateString) => {
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    if(!regex.test(dateString)) {
        return undefined;
    }

    const [year] = dateString.split("-");
    return parseInt(year, 10);
};
